import React, { useCallback, useEffect, useState } from "react";
import { LifeBuoy, Plus, RefreshCw, MessageSquare, Clock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import SupportAttachmentViewer from "@/components/SupportAttachmentViewer";

const STATUS_TONE = {
  open: {
    color: "text-[color:var(--accent-warm)]",
    bg: "bg-[color:rgba(255,138,61,0.08)] border-[color:rgba(255,138,61,0.30)]",
    icon: AlertCircle,
  },
  in_progress: {
    color: "text-[color:var(--brand-primary)]",
    bg: "bg-[color:rgba(27,168,159,0.08)] border-[color:rgba(27,168,159,0.30)]",
    icon: Loader2,
  },
  resolved: {
    color: "text-[color:var(--success)]",
    bg: "bg-[color:rgba(34,197,94,0.08)] border-[color:rgba(34,197,94,0.30)]",
    icon: CheckCircle2,
  },
  closed: {
    color: "text-[color:var(--text-3)]",
    bg: "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]",
    icon: Clock,
  },
  waiting_on_customer: {
    color: "text-[color:var(--warning,#eab308)]",
    bg: "bg-[color:rgba(234,179,8,0.08)] border-[color:rgba(234,179,8,0.40)]",
    icon: Clock,
  },
  archived: {
    color: "text-[color:var(--text-3)]",
    bg: "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]",
    icon: Clock,
  },
};

function fmtStamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function TicketRow({ ticket }) {
  const tone = STATUS_TONE[ticket.status] || STATUS_TONE.open;
  const Icon = tone.icon;
  return (
    <div
      className={`rounded-xl border ${tone.bg} p-4`}
      data-testid={`support-ticket-${ticket.ticket_id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <Icon className={`h-4 w-4 mt-0.5 ${tone.color} shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-semibold text-[color:var(--text-1)]">
              {ticket.subject}
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${tone.bg} ${tone.color}`}
            >
              {ticket.status_label}
            </span>
          </div>
          <div className="text-[11px] text-[color:var(--text-3)] mt-1">
            {ticket.category_label} · Priority: {ticket.priority_label} · Submitted{" "}
            {fmtStamp(ticket.created_at)}
            {ticket.resolved_at ? <> · Resolved {fmtStamp(ticket.resolved_at)}</> : null}
            {ticket.closed_at ? <> · Closed {fmtStamp(ticket.closed_at)}</> : null}
          </div>
          <div className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed whitespace-pre-wrap">
            {ticket.message}
          </div>
          {ticket.attachment ? (
            <div className="mt-3" data-testid={`support-ticket-attachment-${ticket.ticket_id}`}>
              <SupportAttachmentViewer
                apiPath={`/support/tickets/${ticket.ticket_id}/attachment`}
                filename={ticket.attachment.filename}
                sizeBytes={ticket.attachment.size_bytes}
                testId={`support-attachment-${ticket.ticket_id}`}
              />
            </div>
          ) : null}
          {ticket.metadata?.platform || ticket.metadata?.related_post_id ? (
            <div className="text-[11px] text-[color:var(--text-3)] mt-2 flex flex-wrap gap-x-3 gap-y-1">
              {ticket.metadata?.platform ? (
                <span>
                  Platform:{" "}
                  <span className="font-mono text-[color:var(--text-2)]">
                    {ticket.metadata.platform}
                  </span>
                </span>
              ) : null}
              {ticket.metadata?.related_post_id ? (
                <span>
                  Post:{" "}
                  <span className="font-mono text-[color:var(--text-2)]">
                    {ticket.metadata.related_post_id}
                  </span>
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] font-mono">
            ID {ticket.ticket_id}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/support/tickets");
      setTickets(res?.data?.tickets || []);
    } catch (_e) {
      toast.error("Could not load your tickets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <GradientMesh />
      <div className="space-y-6 pb-10" data-testid="support-page">
        <PageHeader
          title="Support"
          subtitle="Report a problem or check the status of tickets you've already opened."
          action={
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={refresh}
                disabled={loading}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="support-refresh"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]"
                data-testid="support-new-ticket"
              >
                <Plus className="h-4 w-4 mr-2" />
                New ticket
              </Button>
            </div>
          }
        />

        <GlassCard className="p-5" data-testid="support-help-card">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)] shadow-[0_4px_12px_rgba(27,168,159,0.35)] shrink-0">
              <LifeBuoy className="h-4 w-4" />
            </div>
            <div className="text-sm text-[color:var(--text-2)] leading-relaxed">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
                Need help? We&apos;re here.
              </div>
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-1)]" data-testid="support-response-guarantee">
                <Clock className="h-3 w-3 text-[color:var(--brand-primary)]" />
                <span><span className="font-semibold">24-Hour Support Response Guarantee</span> — reviewed &amp; responded to within 24 hours or less</span>
              </div>
              <div className="mt-2">
                Submit a ticket below and we&apos;ll review it as soon as possible. Your account
                details, current page, and any related post/platform are auto-attached so we
                can triage faster. We&apos;ll work to resolve your issue as quickly as possible
                after that — some issues may depend on third-party platforms or require
                additional investigation.
              </div>
              <div className="mt-2 text-xs text-[color:var(--text-3)]">
                You can also email{" "}
                <a
                  href="mailto:support@postmoro.com"
                  className="text-[color:var(--brand-primary)] hover:underline"
                  data-testid="support-email-link"
                >
                  support@postmoro.com
                </a>
                .
              </div>
            </div>
          </div>
        </GlassCard>

        <div className="space-y-3" data-testid="support-tickets-list">
          <div className="text-xs uppercase tracking-[0.16em] text-[color:var(--text-3)] inline-flex items-center gap-2">
            <MessageSquare className="h-3 w-3" /> Your tickets
          </div>
          {loading ? (
            <div className="text-sm text-[color:var(--text-3)]" data-testid="support-loading">
              Loading…
            </div>
          ) : tickets.length === 0 ? (
            <GlassCard className="p-6 text-center" data-testid="support-empty">
              <div className="text-sm text-[color:var(--text-2)]">
                You haven&apos;t opened any tickets yet.
              </div>
              <div className="text-xs text-[color:var(--text-3)] mt-1">
                Use “New ticket” when you hit something unexpected — we read every one.
              </div>
            </GlassCard>
          ) : (
            tickets.map((t) => <TicketRow key={t.ticket_id} ticket={t} />)
          )}
        </div>
      </div>

      <ReportProblemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmitted={() => {
          refresh();
        }}
      />
    </>
  );
}
